import RestAPIUtils from './rest-api-utils';

class UserApi {
    static USER_API_CONFIG = {
        apiUrl: process.env.REACT_APP_USER_API_URL,
        apiKey: process.env.REACT_APP_USER_API_KEY,
    };

    static async updateClinicianData(bearerToken, payload) {
        const apiPath = `/v1/clinicians/update`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'PATCH', payload);
    }

    static async getClinicianData(bearerToken, payload) {
        const apiPath = `/v1/clinicians/query`;
        const params = {
            lastUpdated: Date.now(),
        };
        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, params);
    }

    static async updateClinicianDataMultiuser(bearerToken, payload) {
        const apiPath = `/v1/clinicians/update-multiuser`;
        // works via using actions key: createInvitation , acceptInvitation , updateMultiuser , removeMultiuser. Uses the cognito sub of admin making the update
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'PATCH', payload);
    }

    static async getClinicianDataMultiuser(bearerToken, inviteId) {
        const apiPath = `/v1/clinicians/query-multiuser`;
        const params = {
            inviteId: inviteId,
        };
        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, params);
    }

    static async getClinicianDataMultiuserNoAuth(inviteId) {
        const apiPath = `/v1/clinicians/query-multiuser-non-auth`;
        const params = {
            inviteId: inviteId,
        };
        return RestAPIUtils.makeGetRequestNoAuth(UserApi.USER_API_CONFIG, apiPath, params);
    }

    static async generatePresignedUrls(bearerToken, payload) {
        const apiPath = `/v1/user/generate-presigned-urls`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    static async createPresignedImagePutUrl(bearerToken, file) {
        const apiPath = `/v1/user/generate-presigned-urls`;

        const formattedFile = {
            key: file.key,
            fileContentType: file.fileContentType,
            fileSize: file.fileSize,
            method: 'put',
        };

        // Create data object with contentCategory at the root and formattedFile directly
        const data = {
            files: [formattedFile], // API expect an array, so wrap the single file object in an array
            contentCategory: 'clinic_image',
        };

        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', data);
    }

    static async generatePatientLink(
        bearerToken,
        contentIds,
        type = 'handout',
        whiteboardId = null,
        canvasImageLink = null,
        eTag = null,
        updatedByFirstName = null,
        updatedByLastName = null,
        customisedId = null,
        patientDetails = null, // { patientName, clientSurname, patientId, microchipNumber, clinicPatientId }
        shareContext = null, // { sharedViaIntegration, integrationSource }
    ) {
        const apiPath = `/v1/user/content/generate-patient-link`;
        const payload = {
            contentIds: Array.isArray(contentIds) ? contentIds : [contentIds],
            locale: 'en-UK',
            type, // 'type' is always sent
        };

        // Add whiteboard fields if type is 'whiteboard'
        if (type === 'whiteboard') {
            // a bit assumption there is always the main illustration
            if (!whiteboardId || !canvasImageLink || !eTag) {
                console.error('Whiteboard data is missing!');
                throw new Error("Whiteboard ID and canvasImageLink must be provided for type 'whiteboard'");
            }
            payload.whiteboardId = whiteboardId;
            payload.canvasImageLink = canvasImageLink;
            payload.eTag = eTag;
            payload.updatedByFirstName = updatedByFirstName;
            payload.updatedByLastName = updatedByLastName;
        }

        // Add customised handout fields if type is 'customisedHandout'
        if (type === 'customisedHandout') {
            if (!customisedId) {
                console.error('Customised handout ID is missing!');
                throw new Error("Customised handout ID must be provided for type 'customisedHandout'");
            }
            payload.customisedId = customisedId;
            payload.updatedByFirstName = updatedByFirstName;
            payload.updatedByLastName = updatedByLastName;
        }

        // For non-whiteboard/customised types, include updatedBy fields if available
        if (updatedByFirstName && !payload.updatedByFirstName) {
            payload.updatedByFirstName = updatedByFirstName;
        }
        if (updatedByLastName && !payload.updatedByLastName) {
            payload.updatedByLastName = updatedByLastName;
        }

        // Add patient details if provided
        if (patientDetails) {
            if (patientDetails.patientName) payload.patientName = patientDetails.patientName;
            if (patientDetails.clientSurname) payload.clientSurname = patientDetails.clientSurname;
            if (patientDetails.patientId) payload.patientId = patientDetails.patientId;
            if (patientDetails.microchipNumber) payload.microchipNumber = patientDetails.microchipNumber;
            if (patientDetails.clinicPatientId) payload.clinicPatientId = patientDetails.clinicPatientId;
        }
        if (shareContext) {
            if (shareContext.sharedViaIntegration) payload.sharedViaIntegration = true;
            if (shareContext.integrationSource) payload.integrationSource = shareContext.integrationSource;
        }

        console.warn(`generatePatientLink payload: ${JSON.stringify(payload)}`);
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    static async sendPatientEmail(bearerToken, data) {
        const apiPath = `/v1/user/content/send-patient-email`;

        // Construct linkId with clinicId and toggle parameters if provided
        let linkIdWithParams = data.linkId;

        if (data.clinicId) {
            linkIdWithParams += `&clinicId=${data.clinicId}`;
        }

        if (data.toggleStates) {
            const toggleParams = [];
            for (const [key, value] of Object.entries(data.toggleStates)) {
                if (value === false || value === 'false') {
                    toggleParams.push(`${key}=false`);
                }
            }
            if (toggleParams.length > 0) {
                linkIdWithParams += `&${toggleParams.join('&')}`;
            }
        }

        const payload = {
            toEmailAddress: data.toEmailAddress,
            contentName: data.contentName,
            linkId: linkIdWithParams,
        };
        if (data.message) {
            payload.message = data.message;
        }
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    static async sendSurveyData(bearerToken, surveyPayload) {
        const apiPath = `/v1/user/content/send-patient-survey`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', surveyPayload);
    }

    static async sendShortFeedbackData(bearerToken, feedbackPayload) {
        const apiPath = `/v1/user/content/send-short-feedback`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', feedbackPayload);
    }

    static async sendPatientFeedback(bearerToken, topic, message, name) {
        const apiPath = `/v1/user/content/send-patient-feedback`;
        const payload = {
            topic: topic,

            message: message,
            name: name,
        };

        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    static getContentPresignedUrlPayload() {
        return {
            files: [
                {
                    method: 'get',
                    key: 'snapshot.zip',
                },
            ],
            contentCategory: 'content',
        };
    }

    static async generatedContentPresignedUrl(bearerToken) {
        const apiPath = `/v1/user/generate-presigned-urls`;
        const payload = UserApi.getContentPresignedUrlPayload();

        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    static async getUpdatedContent(bearerToken, lastUpdated) {
        const apiPath = '/v1/user/query-updated-content';
        const params = {
            clinicType: process.env.REACT_APP_CETRA_CLINIC_TYPE,
            lastUpdated: lastUpdated,
        };
        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, params);
    }

    static async getPatientsByFilter(bearerToken, params = {}) {
        let queryParams = [];
        // Apply filterType and filterValue if provided
        if (params.filterType && params.filterValue) {
            queryParams.push(`filterType=${encodeURIComponent(params.filterType)}`);
            queryParams.push(`filterValue=${encodeURIComponent(params.filterValue)}`);
        }
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
        const apiPath = `/v1/patients${queryString}`;

        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, {});
    }

    static async createOrUpdatePatient(bearerToken, patientData) {
        const apiPath = `/v1/patients/update`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'PATCH', patientData);
    }

    static async getPatient(bearerToken, patientId) {
        const apiPath = `/v1/patients/query`;
        // Supply patientId as a query param
        const params = { patientId };

        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, params);
    }

    static async createOrUpdateWhiteboard(bearerToken, updatePayload) {
        return RestAPIUtils.makeWriteRequest(
            bearerToken,
            UserApi.USER_API_CONFIG,
            '/v1/whiteboards/update',
            'PATCH',
            updatePayload,
        );
    }

    static async getWhiteboardEntries(bearerToken, filterType = null, filterValue = null) {
        const apiPath = `/v1/whiteboards`;

        const queryParams = {};

        if (filterType) queryParams.filterType = filterType;
        if (filterValue) queryParams.filterValue = filterValue;

        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, queryParams);
    }

    /* ProVet integration */
    static async getProvetClientEmails(bearerToken, { client, userId, timestamp, verification }) {
        const apiPath = `/v1/integrations/provet/client-emails`;
        const params = { client };
        console.log('[UserApi.getProvetClientEmails] calling', apiPath, 'params:', params);
        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, params);
    }

    static async addProvetNote(bearerToken, payload) {
        const apiPath = `/v1/integrations/provet/add-note`;
        console.log('[UserApi.addProvetNote] calling', apiPath);
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    /* Customised Handouts */
    static async createOrUpdateCustomisedHandout(bearerToken, payload) {
        const apiPath = `/v1/customised-handouts/update`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'PATCH', payload);
    }

    static async getCustomisedHandouts(bearerToken, filterType = null, filterValue = null) {
        const apiPath = `/v1/customised-handouts`;

        const queryParams = {};

        if (filterType) queryParams.filterType = filterType;
        if (filterValue) queryParams.filterValue = filterValue;

        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, queryParams);
    }

    /* Saved Regular Handouts */
    static async createOrUpdateSavedHandout(bearerToken, payload) {
        const apiPath = `/v1/saved-handouts/update`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'PATCH', payload);
    }

    static async deleteSavedHandout(bearerToken, payload) {
        const apiPath = `/v1/saved-handouts/delete`;
        return RestAPIUtils.makeWriteRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, 'POST', payload);
    }

    static async getSavedHandouts(bearerToken, filterType = null, filterValue = null) {
        const apiPath = `/v1/saved-handouts`;

        const queryParams = {};

        if (filterType) queryParams.filterType = filterType;
        if (filterValue) queryParams.filterValue = filterValue;

        return RestAPIUtils.makeGetRequest(bearerToken, UserApi.USER_API_CONFIG, apiPath, queryParams);
    }
}

export default UserApi;
